import type {ComputedRef} from 'vue';
import type ProductCatalogService from '~/services/productCatalog/ProductCatalogService';
import type {Gsoa_ProductCatalog_ProductDetail as ProductDetail} from '~/openapi';

export default function useProductCatalogServiceSwitcher<P extends ProductDetail = ProductDetail>(
	factory: ComputedRef<() => Promise<ProductCatalogService<P> | null>>,
) {
	let currentFactory: () => Promise<ProductCatalogService<P> | null> = factory.value;
	let currentPromise: Promise<ProductCatalogService<P> | null> | null = null;
	let currentService: ProductCatalogService<P> | null | undefined;

	async function getService(): Promise<ProductCatalogService<P> | null> {
		// The service is immediately available.
		if (currentService !== undefined) {
			return currentService;
		}

		// The service hasn't even started loading yet.
		if (!currentPromise) {
			currentPromise = currentFactory().then((newService) => {
				currentPromise = null;
				currentService = newService;
				return newService;
			});
		}

		// The service is currently loading.
		return await currentPromise;
	}

	watch(factory, (newFactory) => {
		currentFactory = newFactory;
		currentService?.dispose();
		currentService = undefined;
	});

	tryOnScopeDispose(() => {
		currentService?.dispose();
	});

	return {getService};
}
